import { GameLoop } from './GameLoop';
import { GameStore } from './GameStore';
import { LockstepSession } from '../net/LockstepSession';
import type { SessionStatus } from '../net/LockstepSession';
import { NetworkCommandSource } from '../net/NetworkCommandSource';
import type { Transport } from '../net/Transport';
import { createGrid, createMatch } from '../sim/init';
import type { Grid } from '../sim/path';
import type { MatchConfig } from '../sim/types';
import { MAP_DEFS } from '../content/maps';
import type { MapId } from '../content/maps';

/*
 * הרכבת משחק רשת.
 *
 * **שום דבר כאן אינו חדש** — אלה בדיוק אותם חלקים של משחק יחיד, עם מקור פקודות
 * אחר: ‏`GameStore`, ‏`GameLoop` ו-`step` אינם יודעים שיש רשת. זה מה שהאבסטרקציה
 * ‏`CommandSource` הבטיחה מהיום הראשון, וכאן מתברר שההבטחה הייתה אמיתית.
 *
 * **אין `AiCommandSource`.** שני המושבים אנושיים; מקור רביעי שמייצר פקודות משלו
 * היה חייב לרוץ זהה לחלוטין בשני הצדדים, וזו סיבה מצוינת לא לערבב יריב מחשב
 * למשחק רשת לפני שיש צורך אמיתי.
 *
 * ===========================================================================
 * **השהיה ומהירות משחק מושבתות במשחק רשת. זו החלטה, לא השמטה.** (מלכודת 38)
 *
 * ‏`GameLoop.setPaused` הוא מתג מקומי, והשידור יושב ב-`drain` שרץ רק כשרץ טיק.
 * לכן שחקן שמשהה מפסיק גם לשדר "אין לי פקודות" — והיריב שלו קופא עד שמוכרז
 * אבוד. אותו דבר למהירות: הצד המהיר ימתין ממילא לאיטי, כי `ready()` חוסם.
 *
 * שתי דרכים היו לתקן, ושתיהן יקרות מדי לפיצ'ר הזה:
 *   1. **פקודת השהיה מסונכרנת** — כלומר `Command` חדשה, שדה חדש במצב, כניסה
 *      ל-`hash` ול-`serialize` וטיפול ב-`step`. כלומר: מכניקת סימולציה שלמה,
 *      עם כל מה שנגזר ממנה, כדי שאפשר יהיה ללכת להכין קפה.
 *   2. **להוציא את השידור מ-`drain` לטיימר נפרד** — וזה שובר את התכונה שמונעת
 *      מצד אחד לרוץ קדימה בלי גבול: השליחה כבולה לקצב הסימולציה בכוונה.
 *
 * ולכן: אין השהיה ואין מהירות ברשת, כמו ב-RA2 עצמו. `NetBattle` פשוט אינו מציג
 * את הכפתורים. אם יום אחד תידרש השהיה — היא נכנסת דרך (1) ולא דרך (2).
 * ===========================================================================
 */

export interface NetMatch {
    readonly store: GameStore;
    readonly grid: Grid;
    readonly loop: GameLoop;
    readonly session: LockstepSession;
    readonly source: NetworkCommandSource;
    /** באיזה מושב אנחנו יושבים — 0 או 1. השני הוא היריב. */
    readonly localPlayer: number;
    readonly mapId: MapId;
    /** יוצא מהמשחק: מודיע ליריב, מפרק את הסשן וסוגר את התעבורה. */
    dispose(reason?: string): void;
}

export interface NetMatchOptions {
    readonly transport: Transport;
    readonly config: MatchConfig;
    readonly localPlayer: number;
    readonly mapId: MapId;
    readonly onStatus?: (status: SessionStatus) => void;
}

/**
 * מסננת מזהה מפה שהגיע מהרשת.
 *
 * ה-relay מעביר את המחרוזת כמו שהיא ואינו יודע אילו מפות קיימות — בכוונה, אחרת
 * כל מפה חדשה הייתה מחייבת פריסה של השרת. **מי שיודע הוא הלקוח**, וזו הנקודה
 * שבה מחרוזת מבחוץ הופכת ל-`MapId`.
 */
export function knownMapId(value: string): MapId | null {
    const found = MAP_DEFS.find((map) => map.id === value);
    return found ? found.id : null;
}

export function createNetMatch(options: NetMatchOptions): NetMatch {
    const { transport, config, localPlayer, mapId } = options;

    /*
     * **מצב הפתיחה נבנה בשני הצדדים מאותו `MatchConfig`, ולא עובר ברשת.**
     * זו כל התמצית של lockstep: מעבירים את הזרע ואת הפקודות, לא את המצב.
     */
    const store = new GameStore(createMatch(config));
    const grid = createGrid(mapId);

    const remotePlayers = config.slots
        .map((_slot, index) => index)
        .filter((index) => index !== localPlayer);

    const session = new LockstepSession({
        transport,
        localPlayer,
        remotePlayers,
        readState: () => store.cur,
        ...(options.onStatus ? { onStatus: options.onStatus } : {}),
    });

    const source = new NetworkCommandSource(session);
    const loop = new GameLoop(store, grid, [source]);

    return {
        store,
        grid,
        loop,
        session,
        source,
        localPlayer,
        mapId,
        dispose(reason = 'exit') {
            loop.stop();
            // ההודעה יוצאת לפני הסגירה — אחרת היריב ימתין 15 שניות עד פסק זמן.
            session.leave(reason);
            session.dispose();
            transport.close();
        },
    };
}
